import type { MarketConfiguration } from "@/config/markets/const";
import type { CustomProduct, ProductGroup } from "@apptus/esales-api";
import type { Image, Product } from "types";
import getNorceCdnImageUrl from "./getNorceCdnImageUrl";
import getProductImages from "./getProductImages";
import isExploded from "./isExploded";
import {
  flattenCustomAttributes,
  flattenCustomJson,
  flattenCustomLengths,
  flattenCustomNumbers,
  flattenVariantAttributes,
  getPriceFromVariant,
  type ProductConfig,
} from "./utils";

export function elevateProductToNgProduct({
  product,
  marketConfig,
  config = {},
}: {
  product: CustomProduct;
  marketConfig: MarketConfiguration;
  config: ProductConfig;
}): Product {
  const [variant] = product.variants;
  const { hasVariants = false, allowDefault = true } = config;
  const custom = flattenCustomAttributes(product, marketConfig);
  const numbers = flattenCustomNumbers(product, marketConfig);
  const lengths = flattenCustomLengths(product, marketConfig);
  const json = flattenCustomJson(product);
  const campaignPrice = product.sellingPrice.min < product.listPrice.min;
  const originalPrice = product.listPrice.min;
  const price = product.sellingPrice.min;
  const pricing = getPriceFromVariant(product, marketConfig);
  const images = getProductImages(
    product,
    marketConfig,
    config.promoteProductImages,
  );

  const output: Product = {
    height: product.height?.amount
      ? `${product.height?.amount} ${product.height?.unit}`
      : undefined,
    length: product.length?.amount
      ? `${product.length?.amount} ${product.length?.unit}`
      : undefined,
    width: product.width?.amount
      ? `${product.width?.amount} ${product.width?.unit}`
      : undefined,
    depth: product.depth?.amount
      ? `${product.depth?.amount} ${product.depth?.unit}`
      : undefined,
    weight: product.weight?.amount
      ? `${product.weight?.amount} ${product.weight?.unit}`
      : undefined,
    volume: product.volume?.amount
      ? `${product.volume?.amount} ${product.volume?.unit}`
      : undefined,
    ...custom,
    ...numbers,
    ...lengths,
    ...json,
    id: variant.key,
    type: "Product",
    slug: variant.link,
    ticket: variant.ticket,
    series: product.series,
    ProductURL: variant.link,
    brand: product.brand,
    title: product.title,
    designer: custom.designerName?.at(0) ?? "",
    designers: [custom.designerName].flat(2),
    price: pricing.price,
    priceType: pricing.priceType,
    fromPrice: false,
    campaignPrice,
    originalPrice: pricing.price30,
    presentationPrice: pricing.price,
    presentationOriginal: pricing.price30,
    presentationRrpWhenCampaign: pricing.priceRrp,
    vatRate: numbers.vat,
    recommendedPrice: numbers.rrp,
    stock: numbers.stockOnHand >= 1,
    hasStock: numbers.stockOnHand >= 1,
    flags: [
      ...(custom.variantFlags ?? []),
      ...(custom.productFlags ?? []),
      ...(custom.statuses ?? []),
    ],
    description: product.description ?? "",
    category: custom.totalCategoryCodes?.at(-1) ?? "",
    groupId: custom.productCode?.at(0) ?? "",
    variantAttributes: flattenVariantAttributes(product),
    hasVariants,
    stockStatus: custom.stockStatus?.at(0) ?? "",
    articleStatus: custom.itemStatus?.at(0) ?? "",
    productCode: custom.productCode?.at(0) ?? "",
    brandCode: custom.brandCode?.at(0) ?? "",
    designerCode: custom.designerCode ?? [],
    nextDeliveryDate: custom.nextDeliveryDate?.[0]
      ? new Date(custom.nextDeliveryDate[0])
      : undefined,
    VariantCode: [variant.key],
    images,
    "Brands.Code": custom.brandCode,
    "Designers.Code": custom.designerCode,
    "CategoryPath.Name": custom.totalCategoryCodes,
    "Breadcrumbs.Code": custom.totalCategoryCodes,
    ActualBreadcrumbsCode: custom.breadcrumbsCode_NG,
    Datasheets: json.datasheetsKey,
    Models: json.modelsKey,
  };

  if (!allowDefault) {
    return output;
  }

  if (isExploded(product)) {
    return {
      ...output,
      title: product.typedCustom?.json.variantName ?? output.title,
      fromPrice: false,
    };
  }

  const {
    bestStockStatus,
    defaultProductNG_PartNo: defaultPartNo,
    defaultProductNG_ImageKey: defaultImageKey,
    fromPriceSame,
    fromPriceType = [],
  } = custom;

  const {
    fromPrice = price,
    fromPriceRRP = originalPrice,
    fromPrice30Days = price,
  } = numbers;

  Object.assign(output, {
    stockStatus: bestStockStatus?.at(0) ?? output.stockStatus,
    price: fromPrice ?? output.price,
    fromPrice: !Boolean(fromPriceSame),
    campaignPrice: fromPrice < fromPriceRRP,
    originalPrice: fromPrice30Days,
    presentationPrice: fromPrice,
    presentationRrpWhenCampaign: fromPriceRRP,
    recommendedPrice: fromPriceRRP,
    presentationOriginal: fromPrice30Days,
    priceType: fromPriceType[0],
  });

  if (defaultPartNo) {
    Object.assign(output, {
      ProductURL: product.link + "?variant=" + defaultPartNo[0],
    });
  }

  if (defaultImageKey) {
    const image: Image = {
      src: getNorceCdnImageUrl(defaultImageKey[0], marketConfig),
      alt: product.title,
      id: product.key + "_image_" + defaultImageKey[0],
      type: "image",
    };

    output.images?.splice(0, 1, image);
  }

  return output;
}

export function mapElevateProduct({
  product,
  marketConfig,
  config = {},
}: {
  product: CustomProduct;
  marketConfig: MarketConfiguration;
  config?: ProductConfig;
}): Product {
  return elevateProductToNgProduct({
    product: product as CustomProduct,
    marketConfig,
    config,
  });
}

export function mapElevateProducts(
  productGroup: ProductGroup,
  marketConfig: MarketConfiguration,
  config: ProductConfig = {},
): Product[] {
  return productGroup.products.map((product) =>
    elevateProductToNgProduct({
      product: product as CustomProduct,
      marketConfig,
      config,
    }),
  );
}
